"use client";

import { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouter } from "next/navigation";
import Swal from "sweetalert2";
import Link from "next/link";
import authenticationApi from "@/lib/authenticationApi";
import AuthLayout from "@/components/AuthLayout";

import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Typography,
  Alert,
} from "@mui/material";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: "1rem",
    backgroundColor: "#fff",
    color: "#1e293b",
    "& fieldset": { borderColor: "#cbd5e1" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": {
      borderWidth: "2px",
      borderColor: "#153C7F",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "#fca5a5",
    marginTop: "4px",
  },
};

interface ApiResponse {
  message: string;
  error_message?: string;
}

type SignupFormData = {
  firstName: string;
  lastName: string;
  profession: string;
  customProfession: string;
  email: string;
};

export default function Signup() {
  const router = useRouter();
  const [error, setError] = useState("");
  const [professionOptions, setProfessionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      profession: "",
      customProfession: "",
      email: "",
    },
  });

  useEffect(() => {
    document.title = "CareAscendium - Sign Up";

    const fetchUserRoles = async () => {
      setLoadingRoles(true);
      setRolesError("");
      try {
        const response = await authenticationApi.get("/v1/values/user_roles");
        // Expecting response.data to be an array of roles with id and name or similar
        const options = [
          { value: "", label: "Select a profession" },
          ...(Array.isArray(response.data)
            ? response.data.map((role: any) => ({
                value: role.id || role.ca_value_id || role.code || role.name,
                label: role.name || role.label || role.code,
              }))
            : []),
        ];
        setProfessionOptions(options);
      } catch (err: any) {
        setRolesError("Failed to load professions.");
        setProfessionOptions([
          { value: "", label: "Select a profession" },
          { value: "Other", label: "Other" },
        ]);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchUserRoles();
  }, []);

  const selectedProfession = watch("profession");

  const onSubmit: SubmitHandler<SignupFormData> = async (data) => {
    const submissionData = {
      p_first_name: data.firstName,
      p_last_name: data.lastName,
      p_profession: data.profession || "",
      p_other_profession:
        data.profession === "Other" ? data.customProfession : "",
      p_email: data.email,
    };

    const response = await authenticationApi
      .post(`/v1/registration/register`, submissionData)
      .then((response: any) => {
        if (response.data.status == 200) {
          Swal.fire({
            title: "CareAscendium",
            text: response.data.message,
            icon: "success",
            confirmButtonText: "OK",
          });
          router.push("/login");
        } else {
          setError(response.data.message);
        }
      })
      .catch((error: any) => {
        setError(error.details.message);
      });
  };

  return (
    <AuthLayout
      title="Sign Up"
      welcomeTitle="Welcome to CareAscendium :)"
      leftBackgroundImage="/login-bg.jpg"
      darkTheme
      logoImage="/auth-white-logo.png"
      tagline="QUALITY. PRODUCTIVITY. COMPOUNDED."
      belowCardContent={
        <p className="text-white/90 text-sm font-normal">
          Already have an account?{" "}
          <Link
            href="/login"
            className="text-sky-400 hover:text-sky-300 font-medium underline"
          >
            Login
          </Link>
        </p>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-1.5">
              First Name *
            </label>
            <TextField
              placeholder="John"
              {...register("firstName", { required: "First Name is required" })}
              fullWidth
              variant="outlined"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-1.5">
              Last Name *
            </label>
            <TextField
              placeholder="Smith"
              {...register("lastName", { required: "Last Name is required" })}
              fullWidth
              variant="outlined"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Profession
          </label>
          <FormControl
            fullWidth
            error={!!errors.profession}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "1rem",
                backgroundColor: "#fff",
                color: "#1e293b",
                minHeight: "56px",
                "& fieldset": { borderColor: "#cbd5e1" },
                "&:hover fieldset": { borderColor: "#94a3b8" },
                "&.Mui-focused fieldset": {
                  borderWidth: "2px",
                  borderColor: "#153C7F",
                },
                "& .MuiSelect-select": {
                  color: "#1e293b",
                  padding: "16.5px 14px",
                },
                "& .MuiSvgIcon-root": {
                  color: "#64748b",
                },
              },
            }}
          >
            <Select
              {...register("profession")}
              defaultValue=""
              displayEmpty
              renderValue={(v) => {
                if (v == null || String(v).trim() === "") {
                  return (
                    <span style={{ color: "#94a3b8" }}>
                      Select a profession
                    </span>
                  );
                }
                const opt = professionOptions.find((o) => o.value === v);
                return opt?.label ?? v;
              }}
              disabled={loadingRoles}
              sx={{
                "& .MuiSelect-select": {
                  opacity: 1,
                },
              }}
            >
              {professionOptions.map((option) => (
                <MenuItem key={option.value || "empty"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {loadingRoles && (
              <Typography
                variant="caption"
                sx={{ mt: 1, color: "rgba(255,255,255,0.7)" }}
              >
                Loading professions...
              </Typography>
            )}
            {rolesError && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {rolesError}
              </Typography>
            )}
            {errors.profession && (
              <Typography variant="caption" sx={{ mt: 1, color: "#fca5a5" }}>
                {errors.profession.message}
              </Typography>
            )}
          </FormControl>
        </div>

        {selectedProfession === "Other" && (
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-1.5">
              Specify Other Profession *
            </label>
            <TextField
              placeholder="Your Profession"
              {...register("customProfession", {
                required:
                  selectedProfession === "Other"
                    ? "Please specify your profession"
                    : false,
                minLength: {
                  value: 2,
                  message: "Profession must be at least 2 characters",
                },
              })}
              fullWidth
              variant="outlined"
              error={!!errors.customProfession}
              helperText={errors.customProfession?.message}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Email *
          </label>
          <TextField
            type="email"
            placeholder="Johnsmith@gmail.com"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Please enter a valid email address",
              },
            })}
            fullWidth
            variant="outlined"
            error={!!errors.email}
            helperText={errors.email?.message}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        </div>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{
            py: { xs: 2, sm: 1.75 },
            mt: 0.5,
            borderRadius: "6px",
            backgroundColor: "#63A821",
            color: "#fff",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "0 2px 12px rgba(99,168,33,0.35)",
            "&:hover": {
              backgroundColor: "#4e8c1a",
              boxShadow: "0 4px 14px rgba(99,168,33,0.4)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#63A821",
              color: "#fff",
              opacity: 0.9,
            },
          }}
        >
          {isSubmitting ? "Creating Account..." : "Sign Up"}
        </Button>
      </form>
    </AuthLayout>
  );
}
